// components/layout/UserDropdown.tsx
import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { User, Settings, LogOut } from 'lucide-react';
import { User as UserType } from '../../types/knowledge-base';
import { useAuth0 } from '@auth0/auth0-react';
import { API_ENDPOINTS } from '../../config/api.config';

interface UserDropdownProps {
    user: UserType;
    onShowProfile: () => void;
    onLogout: () => void;
    isMobile?: boolean; // Using isMobile instead of mobileView to match naming convention
}

const UserDropdown: React.FC<UserDropdownProps> = ({
                                                       user,
                                                       onShowProfile,
                                                       onLogout,
                                                       isMobile = false
                                                   }) => {
    const { getAccessTokenSilently } = useAuth0();

    const handlePasswordReset = async () => {
        try {
            const token = await getAccessTokenSilently();

            console.log('Getting password reset URL for:', {
                email: user.email,
                hasToken: !!token
            });

            const response = await fetch(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    email: user.email,
                    connection: 'Username-Password-Authentication'
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Failed to get password reset URL:', errorData);
                throw new Error(errorData.error || 'Failed to get password reset URL');
            }

            const { resetUrl } = await response.json();

            if (!resetUrl) {
                throw new Error('No reset URL received from server');
            }

            // Log before redirect
            console.log('Redirecting to reset URL:', resetUrl);

            // Redirect to the reset URL
            window.location.href = resetUrl;

        } catch (error) {
            console.error('Password reset error:', error);
            alert('There was an error initiating the password reset. Please try again.');
        }
    };

    // Get display name (full name for desktop, nothing for mobile)
    const displayName = user.fullName || '';

    return (
        <Dropdown align="end" className={isMobile ? "mobile-dropdown" : ""}>
            <Dropdown.Toggle
                variant={isMobile ? "link" : "light"}
                className={isMobile
                    ? "d-flex align-items-center p-1 mobile-dropdown-toggle"
                    : "d-flex align-items-center"
                }
            >
                <User size={isMobile ? 24 : 18} className={isMobile ? "" : "me-2"} />
                {!isMobile && displayName}
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Header>User Settings</Dropdown.Header>
                <Dropdown.Item onClick={onShowProfile}>
                    <User size={16} className="me-2" />
                    Edit Profile
                </Dropdown.Item>
                <Dropdown.Item onClick={handlePasswordReset}>
                    <Settings size={16} className="me-2" />
                    Reset Password
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={onLogout}>
                    <LogOut size={16} className="me-2" />
                    Logout
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default UserDropdown;